import type * as React from "react";

export type QuickLauncherActionGroup =
  | "This invoice"
  | "This patient"
  | "Actions"
  | "Navigate"
  | "Go to"
  | "Practices"
  | "Patients"
  | "Preferences";

export interface QuickLauncherAction {
  /** Stable identity — used as the React key and in analytics/tests. */
  id: string;
  /** Primary label shown in the list. */
  label: string;
  /** Optional second line: what the action does, or where it goes. */
  description?: string;
  /** Which section of the list this belongs to; sections render in GROUP_ORDER. */
  group: QuickLauncherActionGroup;
  /** MUI icon component, or one of the repo's `?react` SVG imports. */
  Icon: React.ElementType;
  /**
   * Extra words the fuzzy filter should match on but that aren't worth showing —
   * synonyms, old names, the route path.
   */
  keywords?: string[];
  /**
   * Right-aligned state readout for a toggle-style action ("Visible" / "Hidden").
   * Present only on actions in the Preferences group.
   */
  trailingLabel?: string;
  /**
   * Offered to the internal Suno group only, whatever the feature flag says.
   * Set this on anything administrative — the practice switcher, admin mode,
   * future support tooling — and `useQuickLauncherActions` drops it for
   * everyone else. Prefer this over an `if (isSunoAdmin)` at the call site so
   * the rule has one owner.
   */
  sunoOnly?: boolean;
  /**
   * Low-cardinality name reported to Datadog instead of `id`. Required on any
   * action whose `id` embeds a record id (`patient:123`), because a metric
   * grouped by action name must not fan out per patient.
   */
  telemetryId?: string;
  /** Offered only once the user types: kept out of the default (empty-query) list. */
  searchOnly?: boolean;
  perform: () => void;
}

/**
 * Sections render in this order regardless of registration order, and the
 * keyboard highlight walks the list in exactly this order — see the index
 * assignment in `quick-launcher.component.tsx`.
 *
 * The two context sections come first: they exist only while the user is on
 * the matching page, so when present they are the most likely intent.
 * "Practices" (Suno staff only) sits above "Patients" because a practice id
 * typed by a Suno user also matches patient ids, and switching is why they typed it.
 */
export const GROUP_ORDER: QuickLauncherActionGroup[] = [
  "This invoice",
  "This patient",
  "Actions",
  "Navigate",
  "Go to",
  "Practices",
  "Patients",
  "Preferences"
];
